import { SettingsCache } from '~/lib/consts/settings-cache';
import { Tagged } from '../../Tagged';
import {
  P2GBox,
  SvgAccount,
  SvgGlobe,
  SvgHelpCentre,
  SvgParcel,
  SvgPound,
  SvgRuleWand,
  SvgTools
} from '@parcel2go/core-ui';
import useGoto from '../../../../../hooks/use-goto';
import { RoutingConstants, RoutingConstantsLabels } from '../../../lib/consts/RoutingConstants';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { SuperLink } from './link';
import { routesOfOrders } from '~/routes/groups/orders';
import { routesOfSupport, sanitisedRoutesOfSupport } from '~/routes/groups/support';
import { routesOfAccount, useSanitisedRoutesOfAccount } from '~/routes/groups/account';
import { sanitisedRoutesOfTools } from '~/routes/groups/tools';
import RenderSection from './section';
import OrderNavigationSection from './order';
import { Suspense, useMemo } from 'react';
import { routesOfRewards, sanitisedRoutesOfRewards } from '~/routes/groups/rewards';
import SvgRewards from '~/components/icons/SvgRewards';

export interface props {
  showFullMenuStructure: boolean;
  currentPageRoute: string;
  logEvent: (link: string, close?: boolean) => void;
}

const sanitisedRoute = (route: string) =>
  route
    .replace(/\/:.*$/, '')
    .replace(/[-/]/g, '')
    .toUpperCase();

const ActionMenuContent: React.FC<props> = ({ logEvent, currentPageRoute, showFullMenuStructure }) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const features = SettingsCache.settings.Features;
  const { goTo } = useGoto();
  const sanitisedRoutesOfSupportMemo = useMemo(() => sanitisedRoutesOfSupport(), []);
  const sanitisedRoutesOfToolsMemo = useMemo(() => sanitisedRoutesOfTools(), []);
  const sanitisedRoutesOfRewardsMemo = useMemo(() => sanitisedRoutesOfRewards(), []);
  const sanitisedRoutesOfAccountMemo = useSanitisedRoutesOfAccount();
  const showSidepanelRates = features.includes('RatesSidepanel');
  const isRatesPage = showSidepanelRates && currentPageRoute === RoutingConstants.Pricing;
  const isToolsSelected = sanitisedRoutesOfToolsMemo.includes(currentPageRoute) && !isRatesPage;

  return (
    <Tagged tag="panel-quick-actions">
      <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
        {showFullMenuStructure ? (
          <Suspense>
            <OrderNavigationSection currentPageRoute={currentPageRoute} logEvent={logEvent} />
          </Suspense>
        ) : (
          <SuperLink
            onClick={() => {
              logEvent('orders');
              goTo(RoutingConstants.SmartSend);
            }}
            isSelected={routesOfOrders.includes(currentPageRoute)}
            label={t('SIDEBAR_ORDERS_LINK', { defaultValue: 'Orders' })}
            icon={<SvgParcel />}
          />
        )}

        {showSidepanelRates && (
          <SuperLink
            onClick={() => {
              logEvent('rates');
              goTo(RoutingConstants.Pricing);
            }}
            isSelected={currentPageRoute === RoutingConstants.Pricing}
            label={t('SIDEBAR_RATES_LINK', { defaultValue: 'Rates' })}
            icon={<SvgPound />}
          />
        )}

        {features.includes('RuleManager') && (
          <SuperLink
            onClick={() => {
              logEvent('rulemanager');
              goTo(RoutingConstants.RuleManager);
            }}
            label={t('SIDEBAR_RULE_MANAGER_LINK', { defaultValue: 'Rule Manager' })}
            isSelected={currentPageRoute === RoutingConstants.RuleManager}
            icon={<SvgRuleWand />}
          />
        )}

        {showFullMenuStructure ? (
          <RenderSection
            icon={<SvgParcel />}
            title={t('SIDEBAR_SUPPORT_SECTION_TITLE', { defaultValue: 'Support' })}
            links={sanitisedRoutesOfSupportMemo.map((path) => (
              <SuperLink
                key={path}
                onClick={() => {
                  logEvent('support');
                  goTo(path);
                }}
                isSelected={currentPageRoute === path}
                label={t(
                  `NAV_SUPPORT_${sanitisedRoute(path)}_LINK`.toUpperCase(),
                  RoutingConstantsLabels[path] || path
                )}
              />
            ))}
            openAsDefault={false}
            forceOpen={routesOfSupport.includes(currentPageRoute)}
            storageKey="xs-nav-support-section-state"
          />
        ) : (
          <SuperLink
            onClick={() => {
              logEvent('support');
              goTo(RoutingConstants.Support);
            }}
            isSelected={routesOfSupport.includes(currentPageRoute)}
            label={t('SIDEBAR_SUPPORT_LINK', { defaultValue: 'Support' })}
            icon={<SvgHelpCentre />}
          />
        )}

        {sanitisedRoutesOfRewardsMemo.length > 0 &&
          (showFullMenuStructure ? (
            <RenderSection
              icon={<SvgRewards />}
              title={t('SIDEBAR_REWARDS_SECTION_TITLE', { defaultValue: 'Rewards' })}
              links={sanitisedRoutesOfRewardsMemo.map((path) => (
                <SuperLink
                  key={path}
                  onClick={() => {
                    logEvent('rewards');
                    goTo(path);
                  }}
                  isSelected={currentPageRoute === path}
                  label={t(
                    `NAV_REWARDS_${sanitisedRoute(path)}_LINK`.toUpperCase(),
                    RoutingConstantsLabels[path] || path
                  )}
                />
              ))}
              openAsDefault={false}
              forceOpen={routesOfRewards.includes(currentPageRoute)}
              storageKey="xs-nav-rewards-section-state"
            />
          ) : (
            <SuperLink
              onClick={() => {
                logEvent('rewards');
                if (sanitisedRoutesOfRewardsMemo.includes(RoutingConstants.Referrals)) {
                  goTo(RoutingConstants.Referrals);
                } else if (sanitisedRoutesOfRewardsMemo.includes(RoutingConstants.Rewards)) {
                  goTo(RoutingConstants.Rewards);
                } else {
                  goTo(sanitisedRoutesOfRewardsMemo[0]);
                }
              }}
              label={t('SIDEBAR_REWARDS_LINK', { defaultValue: 'Rewards' })}
              isSelected={routesOfRewards.includes(currentPageRoute)}
              icon={<SvgRewards />}
            />
          ))}

        {showFullMenuStructure ? (
          <RenderSection
            icon={<SvgParcel />}
            title={t('SIDEBAR_ACCOUNT_SECTION_TITLE', { defaultValue: 'Account' })}
            links={sanitisedRoutesOfAccountMemo.map((path) => (
              <SuperLink
                key={path}
                onClick={() => {
                  logEvent('account');
                  goTo(path);
                }}
                isSelected={currentPageRoute === path}
                label={t(
                  `NAV_ACCOUNT_${sanitisedRoute(path)}_LINK`.toUpperCase(),
                  RoutingConstantsLabels[path] || path
                )}
              />
            ))}
            openAsDefault={false}
            forceOpen={routesOfAccount.includes(currentPageRoute)}
            storageKey="xs-nav-account-section-state"
          />
        ) : (
          <SuperLink
            onClick={() => {
              logEvent('settings');
              goTo(sanitisedRoutesOfAccountMemo[0]);
            }}
            isSelected={routesOfAccount.includes(currentPageRoute)}
            label={t('SIDEBAR_SETTINGS_LINK', { defaultValue: 'Account' })}
            icon={<SvgAccount />}
          />
        )}

        <SuperLink
          onClick={() => {
            logEvent('smartsend-dashboard');
            goTo(RoutingConstants.Analytics);
          }}
          isSelected={currentPageRoute === RoutingConstants.Analytics}
          label={t('SIDEBAR_SMARTSEND_ANALYTICS_LINK', { defaultValue: 'Analytics' })}
          icon={<SvgGlobe />}
        />

        {sanitisedRoutesOfToolsMemo.length > 0 &&
          (showFullMenuStructure ? (
            <RenderSection
              icon={<SvgParcel />}
              title={t('SIDEBAR_TOOLS_SECTION_TITLE', { defaultValue: 'Tools' })}
              links={sanitisedRoutesOfToolsMemo.map((path) => (
                <SuperLink
                  key={path}
                  onClick={() => {
                    logEvent('tools');
                    goTo(path);
                  }}
                  isSelected={currentPageRoute === path}
                  label={t(
                    `NAV_TOOLS_${sanitisedRoute(path)}_LINK`.toUpperCase(),
                    RoutingConstantsLabels[path] || path
                  )}
                />
              ))}
              openAsDefault={false}
              forceOpen={isToolsSelected}
              storageKey="xs-nav-tools-section-state"
            />
          ) : (
            <SuperLink
              onClick={() => {
                logEvent('tools');
                if (sanitisedRoutesOfToolsMemo.includes(RoutingConstants.PickAndPacking)) {
                  goTo(RoutingConstants.PickAndPacking);
                } else if (sanitisedRoutesOfToolsMemo.includes(RoutingConstants.Pricing)) {
                  goTo(RoutingConstants.Pricing);
                } else {
                  goTo(sanitisedRoutesOfToolsMemo[0]);
                }
              }}
              label={t('SIDEBAR_TOOLS_LINK', { defaultValue: 'Tools' })}
              isSelected={isToolsSelected}
              icon={<SvgTools />}
            />
          ))}

        {!showFullMenuStructure && features.includes('KnowYourCustomer') && (
          <SuperLink
            onClick={() => {
              logEvent('knowyourcustomer');
              goTo(RoutingConstants.VerifyYourIdentity);
            }}
            isSelected={currentPageRoute === RoutingConstants.VerifyYourIdentity}
            label={t('SIDEBAR_KNOW_YOUR_CUSTOMER_LINK', { defaultValue: 'Verify Your Identity' })}
            icon={<SvgAccount />}
          />
        )}
      </P2GBox>
    </Tagged>
  );
};

export default ActionMenuContent;
